<template>
  <VDialog
    v-model="dialog"
    :fullscreen="fullscreen"
    :persistent="persistent"
    :content-class="currentVariant.class ? currentVariant.class : ''"
    :width="width"
    v-bind="$attrs"
    :attach="attach"
    :scrollable="scrollable"
  >
    <!-- When there is an image, style as horizontal(large) or vertical(small) 'card' -->
    <VCard v-if="img" class="modal__img">
      <VBtn
        icon
        class="modal--close"
        :class="{ 'theme--dark': small }"
        @mousedown="onClose"
      >
        <NsIcon icon-name="icon-close" />
      </VBtn>
      <VLayout
        :class="{ wrap: small }"
        :style="{ height: !small ? '300px' : 'inherit' }"
      >
        <VImg
          :src="img"
          :height="small ? 200 : 300"
          :width="small ? 300 : 200"
        ></VImg>
        <VFlex
          class="modal__content"
          :style="{ height: small ? '300px' : 'inherit' }"
        >
          <VCardTitle v-if="title" class="modal__title">
            {{ title }}
          </VCardTitle>
          <VCardText>
            <!-- @slot Main content of modal -->
            <slot></slot>
          </VCardText>
        </VFlex>
      </VLayout>
    </VCard>
    <VCard v-else>
      <div class="modal__header">
        <div v-if="showIcon" class="modal__variant-title">
          <div class="modal__variant-icon">
            <NsIcon
              :color="currentVariant.class"
              :icon-name="currentVariant.icon"
            />
          </div>
          <div class="modal__variant-text pl-3">
            <div>{{ currentVariant.text }}</div>
          </div>
        </div>
        <VCardTitle v-if="title" class="modal__title">
          {{ title }}
        </VCardTitle>
        <VBtn v-if="!confirm" icon class="modal--close" @mousedown="onClose">
          <NsIcon icon-name="icon-close" />
        </VBtn>
      </div>
      <VCardText
        :style="{ height: scrollable && contentHeight ? contentHeight : '' }"
      >
        <VContainer grid-list-md>
          <!-- @slot main content of modal -->
          <slot></slot>
        </VContainer>
      </VCardText>
      <VCardActions>
        <!-- @slot available method: 'onclose()' -->
        <slot name="actions" :close="onClose"></slot>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script>
/**
 * Original Author: Ben Layton
 * Original Team: Fore oh Four!
 */
import {
  VBtn,
  VCard,
  VCardActions,
  VCardTitle,
  VCardText,
  VContainer,
  VDialog,
  VFlex,
  VImg,
  VLayout,
} from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';
export default {
  name: 'NsModal',
  components: {
    NsIcon,
    VBtn,
    VCard,
    VCardActions,
    VCardTitle,
    VCardText,
    VContainer,
    VDialog,
    VFlex,
    VImg,
    VLayout,
  },
  props: {
    /**
     * Show or hide modal, using `v-model`
     */
    value: {
      type: Boolean,
      required: true,
    },
    /**
     * The title/header of the modal
     */
    title: String,
    /**
     * Modal width will be edge to edge of device
     */
    fullscreen: Boolean,
    /**
     * Force the modal open. Use persistent='false' to disable persistence
     */
    persistent: {
      type: Boolean,
      default: true,
    },
    /**
     * Style modal small, width: 300px
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * Confirm modal. This will have no close button nor icon for modal type (success, info, warning, error)
     */
    confirm: Boolean,
    /**
     * Adds icon that is relevant to a modal type (success, info, warning, error)
     */
    icon: { type: Boolean, default: false },
    /**
     * Modal type of image, styled as a 'card'
     */
    img: String,
    /**
     * Style modal as a info variant
     */
    info: {
      type: Boolean,
      default: false,
    },
    /**
     * Style modal as a success variant
     */
    success: {
      type: Boolean,
      default: false,
    },
    /**
     * Style modal as a error variant
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * Style modal as a warning variant
     */
    warning: {
      type: Boolean,
      default: false,
    },
    /**
     * Custom class
     */
    contextClass: {
      type: String,
      default: '',
    },
    /**
     * Attach modal inside of component instead of at the top of v-app
     */
    attach: {
      type: Boolean,
      default: false,
    },
    /** When set to true, expects a card, card-title, card-text and card-actions. Additionally, card-text should have specified height. Will set card-text to overflow-y */
    scrollable: {
      type: Boolean,
      default: false,
    },
    /** If scrollable this needs to have a value */
    contentHeight: {
      type: String,
      default: '',
    },
  },
  data() {
    return {};
  },
  computed: {
    dialog: {
      get() {
        // iOS hack to prevent background scrolling
        if (this.$vuetify && this.$vuetify.breakpoint.smAndDown) {
          // if dialog is open
          if (this.value) {
            this.hideScroll();
          } else {
            this.showScroll();
          }
        }
        return this.value;
      },
      set(value) {
        /**
         * Tells parent (using v-model) the display value
         * `input` value is needed when using v-model
         * @type {boolean}
         * @param {boolean} value tells parent display value
         */
        this.$emit('input', value);
      },
    },
    currentVariant() {
      if (this.success) {
        return {
          text: 'Success',
          class: 'modal-success',
          icon: 'icon-success-filled',
        };
      } else if (this.warning) {
        return {
          text: 'Warning',
          class: 'modal-warning',
          icon: 'icon-warning-filled',
        };
      } else if (this.error) {
        return {
          text: 'Error',
          class: 'modal-error',
          icon: 'icon-warning-filled',
        };
      } else if (this.info) {
        return {
          text: 'Information',
          class: 'modal-info',
          icon: 'icon-info-filled',
        };
      } else {
        return {
          text: null,
          class: this.contextClass || null,
          icon: null,
        };
      }
    },
    width() {
      // Width doesn't apply for fullscreen
      if (this.fullscreen) {
        return null;
      }
      return this.small ? 300 : 600;
    },
    showIcon() {
      // no icon for confirm modal
      // no icon when type is default
      return !this.confirm && !!this.currentVariant.icon && this.icon;
    },
  },
  methods: {
    /**
     * Sets v-model to false
     *
     * @public
     */
    onClose() {
      this.dialog = false;
      this.$emit('close');
    },
    hideScroll() {
      // iOS hack to prevent background scrolling
      if (this.$root.$el && this.$root.$el.closest('body')) {
        this.$root.$el.closest('body').classList.add('no-scroll');
      }
      window.addEventListener('wheel', this.scrollListener);
      window.addEventListener('keydown', this.scrollListener);
    },
    showScroll() {
      // iOS hack to prevent background scrolling
      if (this.$root.$el && this.$root.$el.closest('body')) {
        this.$root.$el.closest('body').classList.remove('no-scroll');
      }
      window.removeEventListener('wheel', this.scrollListener);
      window.removeEventListener('keydown', this.scrollListener);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

::v-deep * {
  $color-dryink: map.get(typographyVariables.$typography, font-family-primary);
}

.v-dialog__content ::v-deep .v-dialog {
  &.modal-info {
    color: map.get(colors.$themeColors, color-information);
    .modal__variant-icon {
      color: map.get(colors.$themeColors, color-information);
    }
  }
  &.modal-success {
    color: map.get(colors.$themeColors, color-progress);
    .modal__variant-icon {
      color: map.get(colors.$themeColors, color-progress);
    }
  }
  &.modal-warning {
    color: map.get(colors.$themeColors, color-warning);
    .modal__variant-icon {
      color: map.get(colors.$themeColors, color-warning);
    }
  }
  &.modal-error {
    color: map.get(colors.$baseColors, color-error);
    .modal__variant-icon {
      color: map.get(colors.$baseColors, color-error);
    }
  }
  &.modal-notice {
    color: map.get(colors.$themeColors, color-notice);
    .modal__variant-icon {
      color: map.get(colors.$themeColors, color-notice);
    }
  }
}

/* iOS hack: Use when there is no class from external style sheet */
.no-scroll {
  overflow: hidden;
}
.v-dialog__content ::v-deep .v-dialog {
  padding: 0 !important;
  &.modal-success,
  &.modal-error,
  &.modal-warning,
  &.modal-info {
    border-top: 4px solid;
    // background-color: var(--white);
  }
  .modal__img.v-card {
    padding: 0 !important;
    .modal__content {
      padding: 20px;
      overflow-y: auto;
    }
  }
  .v-card {
    padding: 20px;
    border: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
  .container {
    padding-top: 0;
    padding-left: 0;
    padding-right: 0;
  }
  .modal--close {
    position: absolute;
    top: 25px;
    right: 25px;
    margin: -1rem -1rem -1rem auto;
    z-index: 1;
  }
  .v-card__title {
    margin: 0 auto 28px;
    font-size: 18px;
    font-weight: 600;
    height: 24px;
    line-height: 24px;
    letter-spacing: 0;
  }
  .modal__variant-title {
    display: flex;
    margin-bottom: 28px;
    align-items: center;
    font-size: 16px;
    height: 18px;
    line-height: 18px;
  }
}
</style>
